import { Component } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import Pagination from "@mui/material/Pagination";
import Autocomplete from "@mui/material/Autocomplete";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

import DashboardLayout from "ScreenLayouts/ContainerLayout/DashboardLayout";
import DashboardNavbar from "ScreenLayouts/NavbarLayout/DashboardNavbar";
import Footer from "ScreenFooters/DefaultFooter";

import { request } from "app/network";
import { useMaterialUIController } from "context";
import { formatLocalDateTime } from "utils/dateFormatter";
import AddEditTermModal from "./AddEditTermModal";

interface LegalTermsListState {
    terms: any[];
    loading: boolean;
    searchTerm: string;
    pageSize: number;
    pageNumber: number;
    totalCount: number;
    termsTypes: Array<{ code: string; name: string }>;
    modalOpen: boolean;
    selectedTerm: any | null;
    deleteDialogOpen: boolean;
    termToDelete: any | null;
}

const PAGE_SIZE_STORAGE_KEY = "mediinstep_legal_terms_page_size";

class LegalTermsListScreenClass extends Component<any, LegalTermsListState> {
    constructor(props: any) {
        super(props);
        const savedPageSize = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
        const initialPageSize = savedPageSize ? parseInt(savedPageSize, 10) : 20;

        this.state = {
            terms: [],
            loading: false,
            searchTerm: "",
            pageSize: initialPageSize,
            pageNumber: 1,
            totalCount: 0,
            termsTypes: [],
            modalOpen: false,
            selectedTerm: null,
            deleteDialogOpen: false,
            termToDelete: null,
        };
    }

    async componentDidMount() {
        await this.loadTermsTypes();
        this.loadTerms();
    }

    loadTermsTypes = async () => {
        try {
            const res = await request("CommonCode.getByGroup", { pathParams: { groupCode: "TERMS_TYPE" } });
            if (res && res.data) {
                this.setState({
                    termsTypes: res.data.map((item: any) => ({
                        code: item.code,
                        name: item.name,
                    })),
                });
            }
        } catch (error) {
            console.error("약관 유형 조회 실패:", error);
        }
    };

    loadTerms = async () => {
        try {
            this.setState({ loading: true });

            const param = {
                PageNumber: this.state.pageNumber,
                PageSize: this.state.pageSize,
                SearchTerm: this.state.searchTerm || undefined,
            };

            const res = await request("LegalTerms.pagedList", { paramz: param });

            if (res && res.data) {
                const pagedList = res.data;
                this.setState({
                    terms: pagedList.items || [],
                    totalCount: pagedList.totalCount || 0,
                    loading: false,
                });
            } else {
                this.setState({
                    terms: [],
                    totalCount: 0,
                    loading: false
                });
            }
        } catch (error: any) {
            console.error("약관 목록 조회 실패:", error);
            this.setState({ loading: false });
        }
    };

    handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const searchTerm = event.target.value;
        this.setState({ searchTerm, pageNumber: 1 }, () => {
            this.loadTerms();
        });
    };

    handleDelete = (term: any) => {
        this.setState({ deleteDialogOpen: true, termToDelete: term });
    };

    confirmDelete = async () => {
        const { termToDelete } = this.state;
        if (!termToDelete) return;

        try {
            await request("LegalTerms.delete", {
                pathParams: { id: termToDelete.termsSeqId.toString() },
            });
            this.setState({ deleteDialogOpen: false, termToDelete: null });
            this.loadTerms();
        } catch (error: any) {
            console.error("약관 삭제 실패:", error);
            alert(error?.response?.data?.message || "약관 삭제에 실패했습니다.");
        }
    };

    cancelDelete = () => {
        this.setState({ deleteDialogOpen: false, termToDelete: null });
    };

    handlePageChange = (pageIndex: number) => {
        this.setState({ pageNumber: pageIndex + 1 }, () => {
            this.loadTerms();
        });
    };

    handlePageSizeChange = (pageSize: number) => {
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, pageSize.toString());
        this.setState({ pageSize, pageNumber: 1 }, () => {
            this.loadTerms();
        });
    };

    getTermsTypeLabel = (type: string) => {
        const termsType = this.state.termsTypes.find(t => t.code === type);
        return termsType ? termsType.name : type;
    };

    handleCardClick = async (term: any) => {
        try {
            // 약관 상세 조회
            const res = await request("LegalTerms.getById", {
                pathParams: { id: term.termsSeqId.toString() },
            });

            if (res && res.data && res.data.content) {
                // 새 창에서 HTML 표시 (스타일링 없이 content만)
                const newWindow = window.open("", "_blank");
                if (newWindow) {
                    newWindow.document.write(res.data.content);
                    newWindow.document.close();
                }
            }
        } catch (error) {
            console.error("약관 조회 실패:", error);
            alert("약관 내용을 불러오는데 실패했습니다.");
        }
    };

    render() {
        const { loading, totalCount, pageSize, pageNumber, searchTerm } = this.state;

        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox pt={6} pb={3}>
                    {/* 검색 영역 */}
                    <Card>
                        <MDBox p={3}>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                <MDBox display="flex" alignItems="center">
                                    <Icon fontSize="medium">gavel</Icon>
                                    <MDTypography ml={1} variant="h5" fontWeight="medium">
                                        약관 목록 ( LEGAL TERMS LIST )
                                    </MDTypography>
                                </MDBox>
                                <MDButton variant="gradient" color="info" onClick={() => this.setState({ modalOpen: true, selectedTerm: null })}>
                                    <Icon fontSize="small" sx={{ mr: 0.5 }}>
                                        add
                                    </Icon>
                                    약관 추가
                                </MDButton>
                            </MDBox>

                            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                <MDBox display="flex" alignItems="center">
                                    <Autocomplete
                                        disableClearable
                                        value={this.state.pageSize.toString()}
                                        options={["5", "10", "15", "20", "25", "30", "40", "50"]}
                                        renderInput={(params) => (
                                            <MDInput {...params} />
                                        )}
                                        size="small"
                                        sx={{ width: "5rem" }}
                                        onChange={(_, newValue) => {
                                            if (newValue) {
                                                this.handlePageSizeChange(Number(newValue));
                                            }
                                        }}
                                    />
                                    <MDTypography variant="caption" color="secondary">
                                        &nbsp;&nbsp;entries per page
                                    </MDTypography>
                                </MDBox>

                                <MDBox width="12rem" ml="auto">
                                    <MDInput
                                        placeholder="Search..."
                                        value={searchTerm}
                                        onChange={this.handleSearch}
                                        size="small"
                                        fullWidth
                                    />
                                </MDBox>
                            </MDBox>
                        </MDBox>
                    </Card>

                    {/* 리스트 영역 */}
                    <MDBox mt={3}>
                        {loading ? (
                            <MDBox
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                minHeight="400px"
                            >
                                <CircularProgress size={40} sx={{ color: "#49a3f1" }} />
                            </MDBox>
                        ) : this.state.terms.length === 0 ? (
                            <MDBox
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                                minHeight="400px"
                            >
                                <MDTypography variant="h6" color="text">
                                    {searchTerm ? "검색 결과가 없습니다." : "등록된 약관이 없습니다."}
                                </MDTypography>
                            </MDBox>
                        ) : (
                            <>
                                <Grid container spacing={3}>
                                    {this.state.terms.map((term: any) => {
                                        const gridProps = this.props.miniSidenav
                                            ? { xs: 12, sm: 6, md: 4, lg: 4, xl: 2.4 }
                                            : { xs: 12, sm: 6, md: 4, lg: 4, xl: 4 };

                                        const sxProps = this.props.miniSidenav
                                            ? {
                                                '@media (min-width: 1920px)': {
                                                    flexBasis: '16.666667%',
                                                    maxWidth: '16.666667%'
                                                }
                                            }
                                            : {
                                                '@media (min-width: 1920px)': {
                                                    flexBasis: '20%',
                                                    maxWidth: '20%'
                                                }
                                            };

                                        return (
                                            <Grid item {...gridProps} sx={sxProps} key={term.termsSeqId}>
                                                <Card
                                                    sx={{
                                                        height: '100%',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        transition: 'all 0.2s ease-in-out',
                                                        cursor: 'pointer',
                                                        border: this.props.darkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.12)',
                                                        boxShadow: this.props.darkMode
                                                            ? '0 2px 8px rgba(0, 0, 0, 0.3)'
                                                            : '0 2px 8px rgba(0, 0, 0, 0.08)',
                                                        '&:hover': {
                                                            transform: 'translateY(-4px)',
                                                            boxShadow: this.props.darkMode
                                                                ? '0 8px 20px rgba(255, 255, 255, 0.15)'
                                                                : '0 8px 20px rgba(0, 0, 0, 0.15)',
                                                            borderColor: this.props.darkMode ? 'rgba(255, 255, 255, 0.2)' : 'rgba(0, 0, 0, 0.2)',
                                                        },
                                                    }}
                                                    onClick={() => this.handleCardClick(term)}
                                                >
                                                    <MDBox p={3}>
                                                        {/* 헤더 */}
                                                        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                                                            <MDTypography variant="h5" fontWeight="bold">
                                                                {this.getTermsTypeLabel(term.termsType)}
                                                            </MDTypography>
                                                            <Chip
                                                                label={term.isActive ? "활성" : "비활성"}
                                                                color={term.isActive ? "success" : "default"}
                                                                size="small"
                                                                sx={{
                                                                    fontWeight: "medium",
                                                                    ...(term.isActive && {
                                                                        backgroundColor: "#4CAF50",
                                                                        color: "#ffffff",
                                                                        "& .MuiChip-label": {
                                                                            color: "#ffffff",
                                                                        },
                                                                    }),
                                                                }}
                                                            />
                                                        </MDBox>

                                                        <Divider sx={{ my: 2 }} />

                                                        <MDBox>
                                                            {/* 버전 */}
                                                            <MDBox display="flex" alignItems="center" mb={1.5}>
                                                                <Icon fontSize="small" sx={{ mr: 1.5, color: this.props.darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}>
                                                                    tag
                                                                </Icon>
                                                                <MDTypography variant="body2" color="text">
                                                                    버전: <strong>{term.version}</strong>
                                                                </MDTypography>
                                                            </MDBox>

                                                            {/* 제목 */}
                                                            <MDBox display="flex" alignItems="flex-start" mb={1.5}>
                                                                <Icon fontSize="small" sx={{ mr: 1.5, mt: 0.3, color: this.props.darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}>
                                                                    title
                                                                </Icon>
                                                                <MDTypography
                                                                    variant="body2"
                                                                    color="text"
                                                                    sx={{
                                                                        display: '-webkit-box',
                                                                        WebkitLineClamp: 2,
                                                                        WebkitBoxOrient: 'vertical',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        flex: 1,
                                                                    }}
                                                                >
                                                                    {term.title}
                                                                </MDTypography>
                                                            </MDBox>

                                                            {/* 시행일 */}
                                                            <MDBox display="flex" alignItems="center" mb={1.5}>
                                                                <Icon fontSize="small" sx={{ mr: 1.5, color: this.props.darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)' }}>
                                                                    event
                                                                </Icon>
                                                                <MDTypography variant="body2" color="text">
                                                                    시행일: {term.effectiveDate || "-"}
                                                                </MDTypography>
                                                            </MDBox>

                                                            {/* 생성일 및 액션 버튼 */}
                                                            <MDBox display="flex" alignItems="center" justifyContent="space-between">
                                                                <MDBox display="flex" alignItems="center">
                                                                    <Icon fontSize="small" sx={{ mr: 1.5, color: this.props.darkMode ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0.5)' }}>
                                                                        schedule
                                                                    </Icon>
                                                                    <MDTypography variant="caption" color="text.secondary">
                                                                        등록일: {term.crtDt ? formatLocalDateTime(term.crtDt) : "-"}
                                                                    </MDTypography>
                                                                </MDBox>
                                                                <MDBox display="flex" gap={0.5}>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            this.handleDelete(term);
                                                                        }}
                                                                        sx={{
                                                                            color: this.props.darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                                                                            '&:hover': {
                                                                                backgroundColor: this.props.darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                                                                                color: '#f44336',
                                                                            },
                                                                        }}
                                                                    >
                                                                        <Icon fontSize="small">delete</Icon>
                                                                    </IconButton>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            this.setState({ modalOpen: true, selectedTerm: term });
                                                                        }}
                                                                        sx={{
                                                                            color: this.props.darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                                                                            '&:hover': {
                                                                                backgroundColor: this.props.darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
                                                                                color: '#49a3f1',
                                                                            },
                                                                        }}
                                                                    >
                                                                        <Icon fontSize="small">edit</Icon>
                                                                    </IconButton>
                                                                </MDBox>
                                                            </MDBox>
                                                        </MDBox>
                                                    </MDBox>
                                                </Card>
                                            </Grid>
                                        );
                                    })}
                                </Grid>

                                {/* 페이지네이션 */}
                                {totalCount > 0 && (
                                    <MDBox mt={4} display="flex" justifyContent="center">
                                        <Pagination
                                            count={Math.ceil(totalCount / pageSize)}
                                            page={pageNumber}
                                            onChange={(_, page) => this.handlePageChange(page - 1)}
                                            color="primary"
                                            size="large"
                                            showFirstButton
                                            showLastButton
                                            sx={{
                                                '& .MuiPaginationItem-root': {
                                                    color: this.props.darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
                                                },
                                                '& .Mui-selected': {
                                                    backgroundColor: '#49a3f1 !important',
                                                    color: '#ffffff !important',
                                                },
                                            }}
                                        />
                                    </MDBox>
                                )}
                            </>
                        )}
                    </MDBox>
                </MDBox>
                <Footer />

                {/* 약관 추가/수정 모달 */}
                <AddEditTermModal
                    open={this.state.modalOpen}
                    onClose={() => this.setState({ modalOpen: false, selectedTerm: null })}
                    onSuccess={() => {
                        this.loadTerms();
                    }}
                    darkMode={this.props.darkMode}
                    term={this.state.selectedTerm}
                    termsTypes={this.state.termsTypes}
                />

                {/* 삭제 확인 다이얼로그 */}
                <Dialog
                    open={this.state.deleteDialogOpen}
                    onClose={this.cancelDelete}
                    PaperProps={{
                        sx: {
                            backgroundColor: this.props.darkMode ? "#1a2035" : "#fff",
                            color: this.props.darkMode ? "#fff" : "#000",
                        },
                    }}
                >
                    <DialogTitle>
                        <MDTypography variant="h5" fontWeight="medium" component="span">
                            약관 삭제
                        </MDTypography>
                    </DialogTitle>
                    <DialogContent>
                        <MDTypography variant="body2">
                            "{this.state.termToDelete?.title}" 약관을 삭제하시겠습니까?
                            <br />
                            <MDTypography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>
                                이 작업은 되돌릴 수 없습니다.
                            </MDTypography>
                        </MDTypography>
                    </DialogContent>
                    <DialogActions>
                        <MDButton onClick={this.cancelDelete} variant="outlined" color="dark">
                            취소
                        </MDButton>
                        <MDButton onClick={this.confirmDelete} variant="gradient" color="error">
                            삭제
                        </MDButton>
                    </DialogActions>
                </Dialog>
            </DashboardLayout>
        );
    }
}

function LegalTermsListScreen(props: any) {
    const [controller] = useMaterialUIController();
    const { darkMode, miniSidenav } = controller;

    return <LegalTermsListScreenClass {...props} darkMode={darkMode} miniSidenav={miniSidenav} />;
}

export default LegalTermsListScreen;
